import { useState } from 'react'
import { Stepper } from '@shared/ui/Form/Stepper/Stepper'
import { Header } from '@pages/OwnerLanding/ui/Header'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { RegistrationForm } from '@pages/OwnerLanding/RegistrationForm/Forms/RegistrationForm'
import { PlaceInfoForm } from '@pages/OwnerLanding/RegistrationForm/Forms/PlaceInfoForm'
import { PromotionForm } from '@pages/OwnerLanding/RegistrationForm/Forms/PromotionForm'
import { PlaceFormFields, RegFormFields } from '@pages/OwnerLanding/RegistrationForm/Forms/fields'
import { useRouterCompat } from '@shared/lib/hooks/useRouterCompat'
import { API } from '@shared/api'
import type { RestaurantRequestOrganization, RestaurantRequestRestorator } from '@shared/api/types/swaggerTypes'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'

export const OwnerRegistrationForm = () => {
  const router = useRouterCompat()
  const { isMobile } = useClientResponsive()

  const [activeStep, setStep] = useState<number>(0)
  const nextStep = () => setStep((s) => s + 1)
  const prevStep = () => setStep((s) => (s > 0 ? s - 1 : 0))

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [submittedRegFormState, setSubmittedRegFormState] = useState<Partial<Record<RegFormFields, string>>>({})
  const [submittedPlaceFormState, setSubmittedPlaceFormState] = useState<Partial<Record<PlaceFormFields, string>>>({})

  // Сабмит шага "Контакты"
  const onRegistrationSubmit = (localState: typeof submittedRegFormState): Promise<void> => {
    return API.restorator({
      body: {
        ...(Object.keys(localState) as RegFormFields[]).reduce<Record<string, string>>((acc, key) => {
          return { ...acc, [key]: localState[key] as string }
        }, {}),
      } as unknown as RestaurantRequestRestorator,
    }).then(({ uuid }) => setSessionId(uuid))
  }

  // Сабмит шага "О заведении"
  const onPlaceSubmit = (localState: typeof submittedPlaceFormState) => {
    return API.restorator_organization(sessionId, {
      body: {
        ...(Object.keys(localState) as PlaceFormFields[]).reduce<Record<string, string>>((acc, key) => {
          return { ...acc, [key]: localState[key] as string }
        }, {}),
      } as unknown as RestaurantRequestOrganization,
    })
  }

  // Сабмит последнего шага регистрации ("Продвижение")
  const onFinish = (localState: Record<string, unknown>) => {
    API.restorator_promotion(sessionId, { body: localState }).then(() => {
      const landingUrl = `/msk/restaurants/restorator/?regSuccess=${submittedRegFormState[RegFormFields.email]}`
      router.push(landingUrl)
    })
  }

  return (
    <BlockBoundary>
      <Header />

      <main style={{ marginTop: isMobile ? 32 : 65 }}>
        <Stepper activeStep={activeStep}>
          <Stepper.Step title='Контакты'>
            <RegistrationForm
              defaultState={submittedRegFormState}
              onSubmit={(localState) => {
                onRegistrationSubmit(localState).then(() => {
                  setSubmittedRegFormState(localState)
                  nextStep()
                })
              }}
            />
          </Stepper.Step>

          <Stepper.Step title='О заведении'>
            <PlaceInfoForm
              defaultState={submittedPlaceFormState}
              onSubmit={(localState) => {
                onPlaceSubmit(localState).then(() => {
                  setSubmittedPlaceFormState(localState)
                  nextStep()
                })
              }}
              onPrevClick={prevStep}
            />
          </Stepper.Step>

          <Stepper.Step title='Продвижение'>
            <PromotionForm onSubmit={onFinish} onPrevClick={prevStep} />
          </Stepper.Step>
        </Stepper>
      </main>
    </BlockBoundary>
  )
}
